import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
// @ts-expect-error: no types for qrcode
import QRCode from 'qrcode';
import JsBarcode from 'jsbarcode';

Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Regular.ttf', fontWeight: 'normal', fontStyle: 'normal' });
Font.register({ family: 'Open Sans', src: '/fonts/OpenSans-Bold.ttf', fontWeight: 'bold', fontStyle: 'normal' });

const styles = StyleSheet.create({
  page: {
    width: 226.77, // 8cm
    height: 113.39, // 4cm
    padding: 8,
    fontFamily: 'Open Sans',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  row1: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  colQr: {
    width: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  qr: {
    width: 70,
    height: 70,
    marginLeft: 20,
  },
  colInfo: {
    flex: 1,
    justifyContent: 'center',
    paddingLeft: 25,
  },
  assetName: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#222',
    marginBottom: 2,
  },
  assetCode: {
    fontSize: 10,
    color: '#1677ff',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  barcodeContainer: {
    marginTop: 4,
  },
  barcode: {
    width: 120,
    height: 30,
  },
  row2: {
    marginTop: 6,
  },
  companyText: {
    fontSize: 8,
    color: '#888',
    textAlign: 'left',
  },
});

interface AssetLabelPDFProps {
  assetCode: string;
  assetName: string;
  publicUrl: string;
  companyName: string;
  barcodeBase64?: string | null;
  qrCodeBase64?: string | null;
}

// Helper function để tạo barcode base64
const generateBarcodeBase64 = (value: string): string => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 2,
    height: 40,
    fontSize: 12,
    margin: 0,
    displayValue: false,
    background: 'transparent',
    lineColor: '#000',
  });
  return canvas.toDataURL('image/png');
};

const AssetLabelPDF: React.FC<AssetLabelPDFProps> = ({ assetCode, assetName, publicUrl, companyName, barcodeBase64, qrCodeBase64 }) => {
  // Hooks luôn ở đầu component
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [barcodeDataUrl, setBarcodeDataUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!barcodeBase64 || !qrCodeBase64) {
      // Chỉ tạo khi không có props base64
      QRCode.toDataURL(publicUrl, { width: 128, margin: 0 }, (err: Error | null, url: string) => {
        if (!err) {
          setQrDataUrl(url);
        }
      });
      try {
        const barcodeUrl = generateBarcodeBase64(assetCode);
        setBarcodeDataUrl(barcodeUrl);
      } catch (error) {
        // Silent error handling
      }
    }
  }, [publicUrl, assetCode, barcodeBase64, qrCodeBase64]);

  // Ưu tiên dùng base64 props nếu có
  if (barcodeBase64 && qrCodeBase64) {
    return (
      <Document>
        <Page size={{ width: 226.77, height: 113.39 }} style={styles.page}>
          {/* Row 1 */}
          <View style={styles.row1}>
            {/* Cột 1: QR code */}
            <View style={styles.colQr}>
              <Image src={qrCodeBase64} style={styles.qr} />
            </View>
            {/* Cột2 tin */}
            <View style={styles.colInfo}>
              <Text style={styles.assetName}>{assetName}</Text>
              <Text style={styles.assetCode}>{assetCode}</Text>
              <View style={styles.barcodeContainer}>
                <Image src={barcodeBase64} style={styles.barcode} />
              </View>
            </View>
          </View>
        </Page>
      </Document>
    );
  }

  // Fallback: dùng state nếu không có base64 props
  if (!qrDataUrl || !barcodeDataUrl) {
    return null;
  }

  return (
    <Document>
      <Page size={{ width: 226.77, height: 113.39 }} style={styles.page}>
        {/* Row 1 */}
        <View style={styles.row1}>
          {/* Cột 1: QR code */}
          <View style={styles.colQr}>
            <Image src={qrDataUrl} style={styles.qr} />
          </View>
          {/* Cột2 tin */}
          <View style={styles.colInfo}>
            <Text style={styles.assetName}>{assetName}</Text>
            <Text style={styles.assetCode}>{assetCode}</Text>
            <View style={styles.barcodeContainer}>
              <Image src={barcodeDataUrl} style={styles.barcode} />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default AssetLabelPDF; 